import { constant } from '@config/BaseSetting';
import useLocalStorage from '@hooks/useLocalStorage';
import { useGetAuditLogsMutation } from '@services/WebApiService/AuditLogApi';
import { useEffect, useMemo, useState } from 'react';
import { resellersListPath } from '@config/Routes/WebClientRoutes';
import dayjs from 'dayjs';

function normalizeDateFormat(dateString) {
  if (!dateString || typeof dateString !== 'string') return '';
  const date = dayjs(dateString);
  return date.format('DD MMM YYYY hh:mm A Z');
}

function getDefaultDateRange() {
  const now = new Date(Date.now());
  const from = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate(), 0, 0, 0, 0);
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return {
    from,
    to,
  };
}

const initialPagination = { pageIndex: 0, pageSize: 10 };

const initialFilterState = {
  showAdvancedFilter: false,
  search: '',
  dateRange: getDefaultDateRange(),
  activities: [],
  actorRoles: [],
  actors: [],
  sources: [],
  objectTypes: [],
  objects: [],
  status: '',
};

function summarizeItems(items) {
  const more = items.length > 2 ? items.length - 2 : 0;
  return items.slice(0, 2).join(', ') + (more ? `, and ${more} more` : '');
}

function generateMatchText(params) {
  const matchTextItems = [];
  if (params.search) {
    matchTextItems.push(`Log ID/Actor/Object/Description includes: '${params.search}'`);
  }
  if (params.activities?.length) {
    matchTextItems.push(
      `Activity = ${summarizeItems(params.activities.map((item) => item.label))}`
    );
  }
  if (params.sources?.length) {
    matchTextItems.push(`Source = ${summarizeItems(params.sources.map((item) => item.label))}`);
  }
  if (params.actorRoles?.length) {
    matchTextItems.push(`Role = ${summarizeItems(params.actorRoles.map((item) => item.label))}`);
  }
  if (params.actors?.length) {
    matchTextItems.push(`Actor = ${summarizeItems(params.actors.map((item) => item.actor_email))}`);
  }
  if (params.objectTypes?.length) {
    matchTextItems.push(`Object type = ${summarizeItems(params.objectTypes)}`);
  }
  if (params.objects?.length) {
    matchTextItems.push(
      `Object = ${summarizeItems(params.objects.map((item) => item.object_name))}`
    );
  }
  if (params.status) {
    matchTextItems.push(`Status = ${params.status}`);
  }
  return matchTextItems.length ? `Matching: ${matchTextItems.join('; ')}` : '';
}

export default function useAuditLogs({ partnerId }) {
  const [accessToken] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);
  const [fetchParams, setFetchParams] = useState({});
  const [fetchFlag, setFetchFlag] = useState(false);
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedPartnerId, setSelectedPartnerId] = useState();
  const [filterState, setFilterState] = useState(initialFilterState);
  const [matchText, setMatchText] = useState('');
  const [selectedItem, setSelectedItem] = useState();

  const actualPartnerId = useMemo(
    () => partnerId ?? selectedPartnerId,
    [partnerId, selectedPartnerId]
  );

  const [getAuditLogs, getAuditLogsState] = useGetAuditLogsMutation();

  useEffect(() => {
    if (!fetchFlag) return;
    getAuditLogsState.reset();
    if (!fetchParams.partnerId) return;
    getAuditLogs({
      accessToken,
      params: {
        distributor_id: fetchParams.partnerId,
        q: filterState.search || undefined,
        from: filterState.dateRange.from.toISOString(),
        to: filterState.dateRange.to.toISOString(),
        activities: filterState.activities?.map((item) => item.value).join(','),
        sources: filterState.sources?.map((item) => item.value).join(','),
        roles: filterState.actorRoles?.map((item) => item.value).join(','),
        actor_uids: filterState.actors?.map((item) => item.actor_uid).join(','),
        actor_emails: filterState.actors?.map((item) => item.actor_email).join(','),
        object_types: filterState.objectTypes?.join(','),
        object_uids: filterState.objects?.map((item) => item.object_uid).join(','),
        object_names: filterState.objects?.map((item) => item.object_name).join(','),
        statuses: filterState.status,
      },
    });
    setFetchFlag(false);
  }, [fetchParams, fetchFlag]);

  const fetch = (params) => {
    setFetchParams(params);
    setMatchText(generateMatchText(params));
    setFetchFlag(true);
  };

  useEffect(() => {
    getAuditLogsState.reset();
    setFilterState(initialFilterState);
    fetch({ partnerId: actualPartnerId });
  }, [actualPartnerId]);

  const handleAdvancedFilterClick = () => {
    setFilterState({ ...filterState, showAdvancedFilter: !filterState.showAdvancedFilter });
  };

  const handleOnSearchFieldChange = (e) => {
    setFilterState({ ...filterState, search: e.target.value });
  };

  const handleOnDateRangeChange = (newValue) => {
    setFilterState({ ...filterState, dateRange: newValue });
  };

  const handleOnActivitiesChange = (newValue) => {
    setFilterState({ ...filterState, activities: newValue });
  };

  const handleOnActorRolesChange = (newValue) => {
    setFilterState({ ...filterState, actorRoles: newValue });
  };

  const handleOnActorsChange = (newValue) => {
    setFilterState({ ...filterState, actors: newValue });
  };

  const handleOnSourcesChange = (newValue) => {
    setFilterState({ ...filterState, sources: newValue });
  };

  const handleOnObjectTypesChange = (newValue) => {
    setFilterState({ ...filterState, objectTypes: newValue });
  };

  const handleOnObjectsChange = (newValue) => {
    setFilterState({ ...filterState, objects: newValue });
  };

  const handleOnStatusChange = (e) => {
    setFilterState({ ...filterState, status: e.target.value });
  };

  const handlePartnerIdChange = (e) => {
    setSelectedPartnerId(e);
  };

  const handleOnSearchClick = (e) => {
    e.preventDefault();
    fetch({ ...filterState, partnerId: actualPartnerId });
  };

  const handleOnResetClick = () => {
    setFilterState(initialFilterState);
    setSelectedPartnerId(undefined);
    fetch({ ...initialFilterState, partnerId: actualPartnerId });
  };

  const handleOnRefreshClick = () => fetch(fetchParams);

  const handleCloseModal = () => setSelectedItem(undefined);

  const handleOnColumnClicked = (r) => setSelectedItem(r);

  const clickableColumnsList = [
    { id: 1, column: 'id', handler: handleOnColumnClicked },
    {
      id: 2,
      column: 'actor_reseller_name',
      path: (row) => [
        resellersListPath,
        `id=${row.actor_reseller_uid?.split('-')[0]}&name=${row.actor_reseller_name}`,
      ],
    },
  ];

  const logsData = useMemo(() => {
    if (!getAuditLogsState.data?.logs?.length) return [];
    return getAuditLogsState.data.logs
      .slice(
        pagination.pageIndex * pagination.pageSize,
        (pagination.pageIndex + 1) * pagination.pageSize
      )
      .map((l) => ({
        ...l,
        date: normalizeDateFormat(l.created_at),
        status: l.completed ? 'Success' : 'Failed',
        description: l.description || '-',
      }));
  }, [getAuditLogsState.data, pagination]);

  const rowsCount = getAuditLogsState.data?.logs?.length || 0;

  return {
    filterState,
    handleAdvancedFilterClick,
    handleOnSearchFieldChange,
    handleOnDateRangeChange,
    handleOnActivitiesChange,
    handleOnActorRolesChange,
    handleOnActorsChange,
    handleOnSourcesChange,
    handleOnObjectTypesChange,
    handleOnObjectsChange,
    handleOnStatusChange,
    actualPartnerId,
    handlePartnerIdChange,
    handleOnSearchClick,
    handleOnResetClick,
    handleOnRefreshClick,
    handleCloseModal,
    getAuditLogsState,
    matchText,
    logsData,
    rowsCount,
    pagination,
    setPagination,
    clickableColumnsList,
    selectedItem,
  };
}
